/**
 * Multi-window manager — desktop only (Tauri).
 * Handles panel pop-out to separate windows and state sync via BroadcastChannel.
 *
 * Flow:
 * 1. User clicks pop-out button on a panel header.
 * 2. WindowManager creates a new Tauri WebviewWindow loading `?popout=<panelId>`.
 * 3. The secondary window boots in popout mode — only renders the specified panel.
 * 4. State sync via BroadcastChannel (events, store updates).
 * 5. Closing the secondary window notifies main window to restore the panel.
 */

import { panelManager } from '@/panels/panel-manager';
import { eventBus } from '@/core/events';
import { appStore } from '@/core/store';
import { isTauriRuntime } from '@/utils/tauri';
import type { NormalizedEvent } from '@/sources/base-source';

// ── Window position persistence ──

interface WindowState {
  x: number;
  y: number;
  width: number;
  height: number;
}

function getWindowStateKey(panelId: string): string {
  return `sentinel-window-${panelId}`;
}

function loadWindowState(panelId: string): WindowState | null {
  try {
    const raw = localStorage.getItem(getWindowStateKey(panelId));
    if (!raw) return null;
    return JSON.parse(raw) as WindowState;
  } catch { return null; }
}

function saveWindowState(panelId: string, state: WindowState): void {
  try {
    localStorage.setItem(getWindowStateKey(panelId), JSON.stringify(state));
  } catch { /* ignore */ }
}

// ── BroadcastChannel for cross-window state sync ──

const CHANNEL_NAME = 'sentinel-sync';

interface SyncMessage {
  type: 'events' | 'store-update' | 'window-closed' | 'window-opened';
  panelId?: string;
  events?: NormalizedEvent[];
  storeSlice?: Record<string, unknown>;
}

// ── Main window: manages child windows ──

class WindowManager {
  private childWindows = new Map<string, unknown>(); // panelId -> WebviewWindow
  private channel: BroadcastChannel | null = null;
  private syncUnsub: (() => void) | null = null;

  /** Initialize the window manager (main window only). */
  init(): () => void {
    if (!this.isTauri()) return () => {};

    this.channel = new BroadcastChannel(CHANNEL_NAME);

    // Listen for child window close messages
    this.channel.onmessage = (e: MessageEvent<SyncMessage>) => {
      const msg = e.data;
      if (msg.type === 'window-closed' && msg.panelId) {
        this.onChildClosed(msg.panelId);
      }
    };

    // Forward events to child windows
    this.syncUnsub = appStore.subscribe(
      (s) => s.events,
      (events) => {
        if (this.childWindows.size > 0 && this.channel) {
          this.channel.postMessage({ type: 'events', events } satisfies SyncMessage);
        }
      },
    );

    // Listen for pop-out requests from panel headers
    const popoutUnsub = eventBus.on('panel:toggled', ({ panelId, visible }) => {
      if (visible && panelId.startsWith('popout:')) {
        const realId = panelId.slice(7);
        this.popOutPanel(realId);
      }
    });

    return () => {
      popoutUnsub();
      this.syncUnsub?.();
      this.channel?.close();
      this.closeAll();
    };
  }

  /** Pop out a panel to a new window. */
  async popOutPanel(panelId: string): Promise<void> {
    if (this.childWindows.has(panelId)) return; // Already popped out

    const config = panelManager.getPanelConfig(panelId);
    if (!config) return;

    const saved = loadWindowState(panelId);
    const width = saved?.width ?? 500;
    const height = saved?.height ?? 600;
    const x = saved?.x ?? undefined;
    const y = saved?.y ?? undefined;

    try {
      const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow');

      const label = `panel-${panelId}`;
      const url = `${window.location.pathname}?popout=${encodeURIComponent(panelId)}`;

      const child = new WebviewWindow(label, {
        url,
        title: `Sentinel — ${config.title}`,
        width,
        height,
        x,
        y,
        resizable: true,
        decorations: true,
        center: !saved,
      });

      // Wait for window creation
      await child.once('tauri://created', () => {
        this.childWindows.set(panelId, child);
        panelManager.setPoppedOut(panelId, true);

        // Notify via channel
        this.channel?.postMessage({ type: 'window-opened', panelId } satisfies SyncMessage);
      });

      // Handle window close
      await child.once('tauri://destroyed', () => {
        this.onChildClosed(panelId);
      });

      // Save window position/size on move/resize
      await child.onMoved(async () => {
        try {
          const pos = await child.outerPosition();
          const size = await child.outerSize();
          saveWindowState(panelId, {
            x: pos.x, y: pos.y,
            width: size.width, height: size.height,
          });
        } catch { /* ignore */ }
      });

      await child.onResized(async () => {
        try {
          const pos = await child.outerPosition();
          const size = await child.outerSize();
          saveWindowState(panelId, {
            x: pos.x, y: pos.y,
            width: size.width, height: size.height,
          });
        } catch { /* ignore */ }
      });

    } catch (err) {
      console.warn('[Sentinel] Failed to pop out panel:', err);
    }
  }

  /** Handle a child window closing — restore the panel in the main window. */
  private onChildClosed(panelId: string): void {
    this.childWindows.delete(panelId);
    panelManager.setPoppedOut(panelId, false);
  }

  /** Close all child windows. */
  closeAll(): void {
    for (const [panelId, child] of this.childWindows) {
      try {
        (child as { close?: () => Promise<void> }).close?.();
      } catch { /* ignore */ }
      panelManager.setPoppedOut(panelId, false);
    }
    this.childWindows.clear();
  }

  private isTauri(): boolean {
    return isTauriRuntime();
  }
}

/** Singleton window manager. */
export const windowManager = new WindowManager();

// ── Popout mode: secondary window bootstrap ──

/** Check if the current window is a popout panel window. Returns the panel ID or null. */
export function getPopoutPanelId(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('popout');
}

/**
 * Initialize a popout window. Listens for data from the main window
 * via BroadcastChannel and syncs events to the local store.
 * Returns a cleanup function.
 */
export function initPopoutSync(): () => void {
  const channel = new BroadcastChannel(CHANNEL_NAME);

  channel.onmessage = (e: MessageEvent<SyncMessage>) => {
    const msg = e.data;
    if (msg.type === 'events' && msg.events) {
      appStore.setState({ events: msg.events });
    }
  };

  // Notify main window on close
  const panelId = getPopoutPanelId();
  window.addEventListener('beforeunload', () => {
    if (panelId) {
      channel.postMessage({ type: 'window-closed', panelId } satisfies SyncMessage);
    }
  });

  return () => channel.close();
}
